import type { PaletteOptions } from '@mui/material/styles';
import primitivesApp from 'themes/primitives-app';
import primitives from 'themes/primitives';

const { base, gray, blue, green, yellow, red, orange, pink, purple, teal, overlay, jadeite, blueberry, gold, blueAlpha, alpha } = primitivesApp;
const { white, black } = base;
const { brand, gray: pGray, blue: pBlue, green: pGreen, red: pRed, yellow: pYellow, purple: pPurple } = primitives;

const paletteDark: PaletteOptions = {
  // ---- MUI Standard Palette ----
  // Shift lighter so brand colors remain readable on dark surfaces
  primary: {
    light: blue[650],
    main: blue[700],
    dark: blue[930],     // #1f74ba
    contrastText: white,
  },
  secondary: {
    light: blue[500],    // #9bcbeb
    main: blue[650],
    dark: blue[700],
    contrastText: white,
  },
  success: {
    light: green[400],   // #67de6f — brighter
    main: green[500],    // #5ac402 — more vibrant on dark
    dark: green[750],    // #118819
    contrastText: white,
  },
  info: {
    light: blue[400],    // #bfd9fc
    main: blue[650],     // #74b8ff — lighter, more visible on dark
    dark: blue[800],     // #3a72b5
    contrastText: white,
    surface: blue[950],
  },
  warning: {
    light: yellow[400],  // #f5a623
    main: yellow[500],   // #f29d50 — amber, stays visible on dark
    dark: yellow[650],   // #eaa111
    contrastText: white,
  },
  error: {
    light: red[250],     // #ff6e6e — stays vivid on dark
    main: red[400],      // #da6561 — slightly lighter
    dark: red[700],      // #d70015
    contrastText: white,
  },

  // ---- Surface ----
  surface: {
    page: gray[1350],
    default: gray[1300],
    subtle: gray[1200],
    raised: gray[1300],
    overlay: gray[1300],
    sunken: gray[1300],
    disabled: gray[1300],
    inverse: gray[50],
    brand: blue[950],
    brandSubtle: gray[1250],
    success: green[950],
    warning: yellow[900],
    danger: red[900],
    info: blue[1075],
    selected: gray[1250],
    hover: gray[1250],
    positive: green[950],
    positiveStrong: green[600],
    negative: red[900],
    negativeStrong: red[600],
    nav: gray[1300],
    navHover: overlay.white[20],
    navSelected: overlay.white[20],
    accountsBar: gray[1250],
    tableGroupHeader: gray[1250],
    regTableRow: blueAlpha[20],
    chipsSettings: gray[1250],
    subtle2: gray[1250],
    subtle3: gray[1250],
    transaction: gray[1250],
    expense: yellow[900],
    income: green[950],
    overdue: red[900],
    tooltip: gray[200],
    calendar: gray[1300], 
  },

  // ---- Text ----
  // Inverted from light mode — light text on dark backgrounds
  text: {
    primary: gray[50],    // #f5f5f5 — near white, primary readable text
    secondary: gray[550],  // #dfdfdf — secondary, medium light
    tertiary: gray[725],
    disabled: gray[1025],
    contrastText: black,   // For text on light-colored surfaces
    strong: white,         // #ffffff — strongest emphasis
    input: gray[400],      // #ebeff2 — input field text
    warning: yellow[300],
    error: red[250],
    // new from XD
    inverse: gray[1300],
    brand: blue[700],
    brandSubtle: blue[650],
    onBrand: base.white,
    success: green[400],
    danger: red[250],
    info: blue[700],
    positive: green[400],
    negative: red[250],
    placeholder: gray[550],
    onPositive: base.white,
    onNegative: base.white,
    onNav: blue[100],
  },

  // ---- Icons ----
  icon: {
    light: gray[550],    // #c9c7c5 — subtle
    main: gray[300],     // #f4f4f4 — primary icon
    dark: white,         // #ffffff — strongest
    brand: blue[100],
    muted: gray[725],    // #a1afb5
    secondary: gray[725],
    primary: gray[50],
    // new from XD
    tertiary: gray[550],
    disabled: gray[1200],
    inverse: gray[1300],
    onBrand: base.white,
    success: pGreen[400],
    warning: pYellow[400],
    danger: pRed[400],
    info: pBlue[400],
    positive: pGreen[400],
    negative: pRed[400],
    nav: pBlue[50],
  },

  // ---- MUI Action States ----
  // NOTE: XD defines action.{primary,secondary,destructive,ghost,link,custom} as button-variant
  // tokens — a structural conflict with these MUI state primitives. XD button variants are
  // available under `button` below.
  action: {
    active: gray[250],         // #f5f5f5
    hover: gray[1250],
    hoverLight: overlay.white[20],
    hoverOpacity: 0.08,
    selected: blue[800],       // #3a72b5
    selectedDark: overlay.black[20],
    selectedOpacity: 0.08,
    disabled: gray[725],       // #a1afb5
    disabledBackground: gray[1200], // #3e3e3c
    disabledOpacity: 0.45,
    focus: blue[700],          // #5cb3ea
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
    overlay: overlay.white[7],
    surface: blue[930],
    selectedDefault: overlay.white[12],
  },

  // ---- Backgrounds ----
  // Darkest = default canvas; progressively lighter = more elevated
  background: {
    default: gray[1350],
    paper: gray[1300],
    layer1: gray[1025],           // #706e6b — layered container
    subtle: gray[1250],           // #3e3e3c — subtle hover/row targets
    elevated: gray[1250],
    input: gray[1250],
    hint: yellow[700],            // #c67d00 — dark amber warning highlight
    hintSubtle: yellow[900],
    info: blue[990],              // #1a4985 — dark blue info surface
    selected: blue[800],          // #3a72b5 — visible selection bg
    payeeHighlight: green[950],   // #116611 — dark green for payee rules
    settingsBarBackground: blue[990], // #1a4985
    dark: gray[1300],
    authDialog: gray[1400],          // #22262a — matches auth iframe dark background
    sunken: gray[1300],
    positive: green[960],
    positiveStrong: green[600],
  },

  // ---- Backdrop / Overlays ----
  backdrop: {
    fieldHover: overlay.white[10],    // lighten field on hover (dark bg)
    iconDimmed: overlay.black[50], // dims icons (still valid on dark)
    registerSplits: blueAlpha[20], // faint blue tint
    navHover: overlay.white[20],
    navSelected: overlay.white[20],
  },

  // ---- Borders ----
  // Lighter values for visibility against dark backgrounds
  border: {
    default: gray[850],
    light: gray[1025],     // #706e6b — subtle but visible against paper (#3e3e3c)
    main: gray[850],       // #969492 — standard visible border
    strong: gray[1200],
    dark: gray[725],       // #a1afb5 — strong border (inverted: lighter in dark)
    focus: blueberry[100],
    calendarFocus: blueberry[70], // #89a0e6
    // new from XD
    subtle: gray[1200],
    inverse: pGray[900],
    disabled: pGray[700],
    brand: brand[400],
    brandSubtle: brand[800],
    success: pGreen[400],
    warning: yellow[400],
    danger: red[400],
    info: blue[650],
    selected: blue[500],
    shadow: gray[1250],
  },

  divider: overlay.white[10], // white overlay divider for dark surfaces

  // ---- Tooltip ----
  tooltip: {
    light: gray[200],         // #f6f7f8 — light tooltip background
    dark: gray[1250],         // #333333 — dark tooltip background
    text: gray[250],          // #f5f5f5 — readable text on paper-bg tooltip
    contrastText: gray[250],  // #f5f5f5 — text on dark tooltip background
  },

  // ---- State ----
  state: {
    hover: {
      default: pGray[800],
      brand: brand[900],
      danger: pRed[950],
      accountsBar: overlay.white[10],
    },
    pressed: {
      default: pGray[700],
      brand: brand[800],
      danger: pRed[900],
    },
    focus: {
      ring: brand[400],
      ringOffset: pGray[900],
    },
    selected: {
      default: alpha.white[7],
      strong: brand[900],
      accountsBar: overlay.black[20],
    },
    disabled: {
      surface: gray[1200],
      border: pGray[700],
      text: pGray[600],
    },
  },

  // ---- Feedback ----
  feedback: {
    success: {
      surface: pGreen[950],
      surfaceStrong: pGreen[900],
      border: pGreen[700],
      text: pGreen[300],
      icon: pGreen[400],
    },
    warning: {
      surface: yellow[900],
      surfaceStrong: yellow[900],
      border: yellow[700],
      text: yellow[625],
      icon: yellow[625],
    },
    danger: {
      surface: pRed[950],
      surfaceStrong: pRed[900],
      border: pRed[700],
      text: pRed[300],
      icon: pRed[400],
    },
    info: {
      surface: pBlue[950],
      surfaceStrong: pBlue[900],
      border: pBlue[700],
      text: pBlue[300],
      icon: pBlue[400],
    },
    neutral: {
      surface: pGray[900],
      surfaceStrong: pGray[800],
      border: pGray[700],
      text: pGray[300],
      icon: pGray[400],
    },
  },

  // ---- Button Variants (from XD action — kept separate to avoid MUI action conflict) ----
  button: {
    custom: {
      settingsTabSelected: gray[1200],
    },
    primary: {
      surface: blue[930],
      surfaceHover: blue[950],
      surfacePressed: blue[500],
      surfaceDisabled: gray[1300],
      text: base.white,
      textDisabled: gray[1200],
      icon: base.white,
    },
    secondary: {
      surface: base.transparent,
      surfaceHover: blue[1075],
      surfacePressed: blue[1075],
      surfaceDisabled: base.transparent,
      border: blue[700],
      borderHover: blue[500],
      borderDisabled: gray[1300],
      text: blue[700],
      textDisabled: gray[1200],
      icon: blue[700],
    },
    destructive: {
      surface: pRed[600],
      surfaceHover: pRed[500],
      surfacePressed: pRed[400],
      surfaceDisabled: pGray[800],
      text: base.white,
      textDisabled: pGray[600],
      icon: base.white,
    },
    ghost: {
      surface: base.transparent,
      surfaceHover: pGray[800],
      surfacePressed: pGray[700],
      surfaceDisabled: base.transparent,
      text: brand[400],
      textDisabled: pGray[600],
      icon: brand[400],
    },
    link: {
      text: blue[650],
      textHover: brand[300],
      textVisited: pPurple[400],
      textDisabled: pGray[600],
    },
  },

  // ---- Overlay Scale ----
  overlayScale: {
    black10: alpha.black[10],
    black20: alpha.black[20],
    black30: alpha.black[30],
    black40: alpha.black[40],
    black50: alpha.black[50],
    black60: alpha.black[60],
    white10: alpha.white[10],
    white20: alpha.white[20],
    white30: alpha.white[30],
    white40: alpha.white[40],
    white50: alpha.white[50],
    white60: alpha.white[60],
  },

  // ---- Accent ----
  accent: {
    positive: green[400],
    negative: red[250],
  },

  // ---- Amount / Numbers ----
  // Brighter/lighter for readability on dark backgrounds
  amount: {
    positive: green[400],  // #67de6f — brighter green
    negative: red[250],    // #ff6e6e — lighter red
  },

  // ---- Charts ----
  charts: {
    positive: green[400],
    negative: red[250],
  },

  // ---- Data Sequence (named, from XD) ----
  dataSequence: {
    blue: blue[650],
    yellow: yellow[750],
    green: green[400],
    orange: orange[500],
    purple: purple[100],
    pink: pink[200],
    teal: teal[100],
    purple2: purple[300],
    teal2: teal[400],
    orange2: orange[600],
  },

  // ---- Avatar ----
  avatar: {
    green: green[750],
    blue: blue[950],
    gray: gray[1025],
    grayLarge: gray[1300],
  },

  // ---- Badges ----
  badges: {
    orange: yellow[900],
  },

  // ---- Severity Status ----
  status: {
    lowLight: green[400],    // #67de6f — brighter
    mediumMain: yellow[400], // #f5a623 — amber, visible on dark
    highMain: red[250],      // #ff6e6e — lighter red
    attentionLight: gold[30],  // #f9efd4 — stays
    attentionMain: gold[100],  // #eac563 — stays
    dangerMain: red[400],    // #da6561 — slightly lighter
    incomeLight: green[900],   // #008e38 — dark green for calendar cells
    expenseLight: yellow[700], // #c67d00 — dark amber for calendar cells
    overdueLight: red[900],    // #330000 — dark red for calendar cells
    surfaceStrong: yellow[900],
  },

  // ---- Data Visualization (indexed array, legacy) ----
  // Brighter/more vibrant palette for charts on dark backgrounds
  data: {
    sequence1: [
      blue[650], yellow[750], green[400], orange[500],
      purple[100], pink[200], teal[100], purple[300],
      teal[400], orange[600],
    ],
  },

  // ---- Legend ----
  legend: {
    income: green[400],  // #67de6f — brighter for dark bg
    bill: blue[500],     // #9bcbeb — lighter for dark bg
  },

  // ---- Links ----
  link: {
    main: blue[500],       // #9bcbeb — light, readable on dark
    dark: blue[650],       // #74b8ff
    interactive: blue[650], // #74b8ff
  },

  // ---- App-Specific ----
  other: {
    overdueTxn: yellow[400],  // #f5a623 — more visible on dark
    dueTxn: blue[500],        // #9bcbeb — lighter
    scheduledChip: yellow[300], // #f5dbb5 — stays
  },

  reports: {
    incomeColor: jadeite[100],  // #62b59e — stays (unique brand color)
    positiveColor: green[400],  // #67de6f — brighter on dark
    negativeColor: red[250],    // #ff6e6e — lighter on dark
    positiveDimmed: green[200], // #add7ad — more visible than light mode's 100
    negativeDimmed: red[200],   // #ef7d5b — more vibrant on dark
  },

  checkbox: {
    background: gray[1300],
    border: gray[850],
    color: gray[0],
  },

  cuecards: {
    surface: blue[1025],
  },

  icons: {
    svgFilter: 'invert(1)',
  },

};

export default paletteDark;
